import type { ApolloQueryResult } from '@apollo/client';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';

import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import { WidgetMoonphase } from '@/components/WidgetMoonphase/WidgetMoonphase';
import type {
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulSelectorModalByMarketingPartnerQuery,
} from '@/graphql/contentful/api';
import { DetailPageDocument } from '@/graphql/contentful/api';
import type {
  ProductsByMarketingPartnerQuery,
  ProductVariantWatchBasicDetailsFragment,
  ProductVariantWatchDetailsFragment,
} from '@/graphql/saleor/api';
import { useSendLandingAlgoliaEvent } from '@/hooks/useSendLandingAlgoliaEvent';
import type {
  CmsExtraDataRecord,
  ContentfulData,
} from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import {
  getDropdownValue,
  getMultiselectValue,
  getNameValue,
} from '@/lib/attributes';
import { getTranslatedName } from '@/lib/i18n';
import { getProductType } from '@/lib/product';
import type { ProductStructuredData } from '@/lib/seo/SeoVariables';
import { formatProductSlug } from '@/lib/util';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useLocale } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { WatchSectionsOpts } from '@/views/WatchView/cms';
import {
  getEnhancedCmsData,
  renderContentfulWatchSection,
} from '@/views/WatchView/cms';
import { useFetchProductData } from '@/views/WatchView/ssr';

import { PdpCrawlableContent } from './components/PdpCrawlableContent';
import { useRecentlyViewedProducts } from './helper';

export type PdpCmsData = ContentfulData & { type: string };
export interface WatchViewProps {
  businessData: any;
  cmsData: PdpCmsData[];
  cmsExtraDataRecord: CmsExtraDataRecord;
  marketingPartnerProductsResult: ApolloQueryResult<ProductsByMarketingPartnerQuery>;
  marketingSelectorModalResult:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  productStructuredData?: ProductStructuredData;
  productVariant: ProductVariantWatchBasicDetailsFragment;
}

export const WatchView = ({
  businessData,
  cmsData,
  cmsExtraDataRecord,
  marketingPartnerProductsResult,
  marketingSelectorModalResult,
  productVariant,
}: WatchViewProps) => {
  const draftMode = useContext(DraftModeContext);
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: DetailPageDocument,
  });
  const {
    product: { collections, compatibleStraps, media, relatedProducts },
    sku,
  } = productVariant;

  const relatedProductReference = getMultiselectValue(relatedProducts);
  const compatibleStrapsReference = getMultiselectValue(compatibleStraps);
  const name = getTranslatedName('name', productVariant.product);
  const product = productVariant?.product;
  const modelStory = getDropdownValue(product?.modelStory);
  const warrantyDuration = getDropdownValue(product?.warrantyDuration);
  const warrantyExtDuration = getDropdownValue(
    product?.warrantyExtensionDuration,
  );
  const movementManufacture = getDropdownValue(product?.movementManufacture);
  const recentlyViewedProductReference = useRecentlyViewedProducts(sku);
  useSendLandingAlgoliaEvent(sku);

  const purchaseBenefits = useMemo(() => {
    const perksSection = cmsData.find(
      (d) => d.__typename === 'PlaceholderProductDetails',
    ) as ContentfulPlaceholderProductDetailsFragment | undefined;
    const items = perksSection?.perksCollection?.items ?? [];
    return items
      .map((item) => item?.heading)
      .filter((heading): heading is string => !!heading);
  }, [cmsData]);

  const [clientSideSectionOpts, setClientSideSectionOpts] =
    useState<WatchSectionsOpts>([]);
  const { productCatalogMode } = useRegionProvider();

  const locale = useLocale();
  const liveProductData = useFetchProductData(
    sku ?? '',
    (locale ?? '') as string,
    productCatalogMode,
  );

  const liveBusinessData = liveProductData?.businessData?.productVariant;
  const liveProductVariantData = liveProductData?.variantDetailsData
    ?.productVariant as unknown as ProductVariantWatchDetailsFragment;

  const cmsSectionOpts: WatchSectionsOpts = [
    {
      type: 'PlaceholderProductDetails',
      opts: {
        businessData: liveBusinessData || businessData?.productVariant,
        marketingPartnerProductsResult: marketingPartnerProductsResult,
        marketingSelectorModalResult: marketingSelectorModalResult,
        variantBasicDetails: productVariant,
        variantDetails:
          liveProductVariantData ||
          (productVariant as unknown as ProductVariantWatchDetailsFragment),
      },
    },
    {
      type: 'PlaceholderModelStory',
      opts: {
        children: modelStory,
        images: media,
      },
    },
    {
      type: 'SliderProductsAutomatic',
      opts: {
        type: SliderProductsTypeEnum.YouMayAlsoLike,
        filter: { slugs: relatedProductReference },
        excludeSlug: productVariant.product.slug,
        currentProductSku: sku ?? undefined,
      },
    },
    {
      type: 'SliderProductsAutomatic',
      opts: {
        type: SliderProductsTypeEnum.CompatibleStraps,
        filter: { slugs: compatibleStrapsReference },
        sizes: '50vw',
      },
    },
    {
      type: 'Placeholder',
      opts: {
        type: 'ugc',
        collectionName: collections?.[0]?.name ?? '',
        name: name,
        reference: sku!,
      },
    },
    {
      type: 'Placeholder',
      opts: {
        type: 'key features',
        product,
      },
    },
    {
      type: 'Placeholder',
      opts: {
        type: 'technical data',
        product,
        productType: getProductType(product.productType) as 'watch' | 'eyewear',
        sku,
      },
    },
    {
      type: 'Placeholder',
      opts: {
        type: 'register watch',
      },
    },
  ];

  const clientOnlySections: Array<{ type: string; typeName: string }> = [
    {
      typeName: 'SliderProductsAutomatic',
      type: 'recently viewed',
    },
    {
      typeName: 'Placeholder',
      type: 'warranty',
    },
  ];

  useEffect(() => {
    const opts: WatchSectionsOpts = [
      {
        type: 'SliderProductsAutomatic',
        opts: {
          type: SliderProductsTypeEnum.RecentlyViewed,
          filter: {
            slugs: recentlyViewedProductReference.map(formatProductSlug),
          },
          excludeSlug: productVariant.product.slug,
        },
      },
      {
        type: 'Placeholder',
        opts: {
          type: 'warranty',
          warrantyDuration,
          warrantyExtDuration,
          movementManufacture,
        },
      },
    ];
    setClientSideSectionOpts(opts);
  }, [recentlyViewedProductReference]);

  const isMoonphaseWatch = ['TECH-808890', 'TECH-74757571111']?.includes(
    getNameValue(product?.calendar),
  );

  const pdpSections = liveCmsData.map((data, i, items) => {
    const key = generateSectionKey(data, i);
    const enhancedCmsData = getEnhancedCmsData(data, i, items);
    const isClientOnly =
      clientOnlySections.find((e) => {
        return data.__typename === e.typeName && data?.type === e.type;
      }) || false;

    if (i === 2 && isMoonphaseWatch) {
      return (
        <Fragment key='widgetMoonphase'>
          <WidgetMoonphase />
        </Fragment>
      );
    }

    if (!isClientOnly) {
      return (
        <Fragment key={key}>
          {renderContentfulWatchSection(
            enhancedCmsData,
            cmsSectionOpts,
            cmsExtraDataRecord,
            draftMode,
          )}
        </Fragment>
      );
    } else {
      return (
        <Fragment key={key}>
          {renderContentfulWatchSection(
            enhancedCmsData,
            clientSideSectionOpts,
            cmsExtraDataRecord,
            draftMode,
          )}
        </Fragment>
      );
    }
  });

  return (
    <>
      {pdpSections}
      <PdpCrawlableContent
        purchaseBenefits={purchaseBenefits}
        relatedProducts={relatedProductReference}
        warrantyDuration={warrantyDuration}
        warrantyExtDuration={warrantyExtDuration}
      />
    </>
  );
};

WatchView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      alertBannerHiddenOnMobile
      cmsData={page.props.cmsDefaultLayoutData}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
